import React, { useEffect, useMemo, useRef, useState } from "react";
import { createMediaAsset, findDuplicateMediaAsset, listMediaAssets } from "../auth/apiMediaLibrary";
import { toImageUrl } from "../../config";
import "./mediaLibrary.css";

const labels = {
  upload: "Importer",
  library: "Bibliothèque",
  url: "URL",
};

const safeUrl = (value) => {
  const raw = String(value || "").trim();
  if (raw.startsWith("/") && !raw.startsWith("//")) return raw;
  try {
    const parsed = new URL(raw);
    return ["http:", "https:"].includes(parsed.protocol) && !parsed.username && !parsed.password ? parsed.toString() : "";
  } catch {
    return "";
  }
};

const fileBaseName = (name) => String(name || "Media").replace(/\.[^.]+$/, "").slice(0, 150) || "Media";

const checksumFile = async (file) => {
  if (!file?.arrayBuffer || typeof window === "undefined" || !window.crypto?.subtle) return "";
  const digest = await window.crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
};

/**
 * Reusable three-source media selector. The caller keeps its historical
 * uploader through `onUpload`; cataloguing is a separate, unchecked-by-
 * default choice, so editor-only uploads retain their previous storage.
 */
export default function UniversalMediaPicker({
  kind = "image",
  accept,
  onUpload,
  onSelect,
  value = "",
  compact = false,
}) {
  const [tab, setTab] = useState("upload");
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [search, setSearch] = useState("");
  const [url, setUrl] = useState("");
  const [state, setState] = useState({ loading: false, assets: [], error: "" });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const requestedKind = kind === "video" ? "video" : "image";

  useEffect(() => {
    if (tab !== "library") return undefined;
    let active = true;
    setState((current) => ({ ...current, loading: true, error: "" }));
    const timer = setTimeout(() => {
      listMediaAssets({ kind: requestedKind, search, limit: 60, sort: "newest" })
        .then((result) => { if (active) setState({ loading: false, assets: result.assets || [], error: "" }); })
        .catch((error) => { if (active) setState({ loading: false, assets: [], error: error.message }); });
    }, 180);
    return () => { active = false; clearTimeout(timer); };
  }, [requestedKind, search, tab]);

  const catalog = async (media, source, fallbackName) => {
    const mediaUrl = media?.url || media?.path;
    if (!saveToLibrary || !mediaUrl) return { ...media, url: mediaUrl };
    const response = await createMediaAsset({
      kind: requestedKind,
      url: mediaUrl,
      name: fileBaseName(media?.originalName || fallbackName),
      originalName: media?.originalName || fallbackName || "",
      mimeType: media?.mimeType || media?.type || "",
      size: media?.size || media?.byteLength || 0,
      width: media?.width || media?.optimization?.width || 0,
      height: media?.height || media?.optimization?.height || 0,
      checksum: media?.checksum || media?.optimization?.hash || "",
      thumbnailUrl: media?.thumbnailUrl || "",
      source,
      shared: true,
    });
    return { ...media, url: response.asset.url, assetId: response.asset._id, asset: response.asset, duplicate: response.duplicate };
  };

  const chooseFile = async (file) => {
    if (!file || !onUpload) return;
    setBusy(true);
    setState((current) => ({ ...current, error: "" }));
    try {
      const checksum = saveToLibrary ? await checksumFile(file) : "";
      if (checksum) {
        const known = await findDuplicateMediaAsset({ checksum, kind: requestedKind });
        if (known.duplicate && known.asset) {
          onSelect?.({ url: known.asset.url, assetId: known.asset._id, asset: known.asset, duplicate: true });
          return;
        }
      }
      const uploaded = await onUpload(file);
      const selected = await catalog({ ...(uploaded || {}), checksum: uploaded?.checksum || uploaded?.optimization?.hash || checksum }, "upload", file.name);
      onSelect?.(selected);
    } catch (error) {
      setState((current) => ({ ...current, error: error.message || "Import impossible." }));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const chooseUrl = async () => {
    const normalized = safeUrl(url);
    if (!normalized) {
      setState((current) => ({ ...current, error: "Utilisez une URL HTTP(S) valide." }));
      return;
    }
    setBusy(true);
    try {
      const selected = await catalog({ url: normalized }, "url", normalized.split("/").pop() || "Media URL");
      onSelect?.(selected);
      setUrl("");
      setState((current) => ({ ...current, error: "" }));
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setBusy(false);
    }
  };

  const preview = useMemo(() => value ? (requestedKind === "video"
    ? <video src={toImageUrl(value)} controls preload="metadata" style={{ width: "100%", maxHeight: 150 }} />
    : <img src={toImageUrl(value)} alt="" loading="lazy" style={{ width: "100%", maxHeight: 130, objectFit: "contain" }} />) : null, [requestedKind, value]);

  return (
    <div className="elakay-media-picker" data-compact={compact ? "true" : "false"}>
      <div className="elakay-media-picker__tabs" role="tablist" aria-label="Source du média">
        {Object.keys(labels).map((key) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => { setTab(key); setState((current) => ({ ...current, error: "" })); }}>
            {labels[key]}
          </button>
        ))}
      </div>
      <div className="elakay-media-picker__body">
        {tab === "upload" && (
          <div>
            <div className="elakay-media-picker__row">
              <input ref={inputRef} type="file" aria-label={requestedKind === "video" ? "Importer une vidéo" : "Importer une image"} accept={accept || (requestedKind === "video" ? "video/mp4,video/webm,video/quicktime" : "image/jpeg,image/png,image/webp,image/gif")} disabled={busy} onChange={(event) => chooseFile(event.target.files?.[0])} />
              {busy && <span className="elakay-media-picker__message">Import en cours…</span>}
            </div>
            <label className="elakay-media-picker__save">
              <input type="checkbox" checked={saveToLibrary} onChange={(event) => setSaveToLibrary(event.target.checked)} />
              Enregistrer également dans la bibliothèque Elakay
            </label>
          </div>
        )}
        {tab === "library" && (
          <div>
            <input className="elakay-media-picker__input" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher dans la bibliothèque…" />
            {state.loading ? (
              <div className="elakay-media-picker__grid" aria-busy="true">{Array.from({ length: 6 }, (_, index) => <div className="elakay-media-picker__skeleton" key={index} />)}</div>
            ) : state.assets.length ? (
              <div className="elakay-media-picker__grid">
                {state.assets.map((asset) => (
                  <button className="elakay-media-picker__card" type="button" key={asset._id} onClick={() => onSelect?.({ url: asset.url, assetId: asset._id, asset, kind: asset.kind, width: asset.width, height: asset.height, mimeType: asset.mimeType })}>
                    {asset.kind === "video" ? <video src={toImageUrl(asset.thumbnailUrl || asset.url)} muted playsInline preload="metadata" /> : <img src={toImageUrl(asset.thumbnailUrl || asset.url)} alt={asset.altText || asset.name} loading="lazy" decoding="async" />}
                    <span title={asset.name}>{asset.name}</span>
                  </button>
                ))}
              </div>
            ) : <p className="elakay-media-picker__message">Aucun média compatible.</p>}
          </div>
        )}
        {tab === "url" && (
          <div>
            <div className="elakay-media-picker__row">
              <input className="elakay-media-picker__input" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://…" onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); chooseUrl(); } }} />
              <button className="elakay-media-picker__button elakay-media-picker__button--primary" type="button" onClick={chooseUrl} disabled={busy || !url.trim()}>Utiliser</button>
            </div>
            <label className="elakay-media-picker__save">
              <input type="checkbox" checked={saveToLibrary} onChange={(event) => setSaveToLibrary(event.target.checked)} />
              Enregistrer également dans la bibliothèque Elakay
            </label>
          </div>
        )}
        {state.error && <p className="elakay-media-picker__message elakay-media-picker__message--error" role="alert">{state.error}</p>}
        {preview && <div style={{ marginTop: 10, borderRadius: 9, overflow: "hidden", background: "#f6f3fc" }}>{preview}</div>}
      </div>
    </div>
  );
}

export { safeUrl, fileBaseName, checksumFile };
